import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  MUTATION_APPROVE_BIG_QUERY_SYNC_JOB,
  MUTATION_REJECT_BIG_QUERY_SYNC_JOB,
  MUTATION_START_BIG_QUERY_SYNC_PREVIEW,
} from '@/graphql/mutation/bigQuerySyncPreview';
import {
  QUERY_BIG_QUERY_SYNC_PREVIEW_DIFF_PAGE,
  QUERY_BIG_QUERY_SYNC_PREVIEW_STATUS,
} from '@/graphql/queries/bigQuerySyncPreview';
import type {
  BigQuerySyncJobGql,
  BigQuerySyncPreviewDiffLineGql,
} from '@/graphql/types/bigQuerySyncPreview';
import {
  canApproveOrRejectBigQuerySyncJob,
  isTerminalBigQuerySyncJobStatus,
} from '@/services/bigQuerySyncPreview/bigQuerySyncPreviewJobState';

const STATUS_POLL_MS = 2800;

export function useBigQuerySyncPreview() {
  const client = useApolloClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const [startBlockedPayload, setStartBlockedPayload] = useState<{ drift: unknown | null } | null>(
    null
  );
  const [diffLines, setDiffLines] = useState<BigQuerySyncPreviewDiffLineGql[]>([]);
  const [diffNextCursor, setDiffNextCursor] = useState<string | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffFetchError, setDiffFetchError] = useState<string | null>(null);
  const [entityInput, setEntityInput] = useState('');
  const [entityApplied, setEntityApplied] = useState<string | undefined>(undefined);

  const latestJobStatusRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!jobId) latestJobStatusRef.current = undefined;
  }, [jobId]);

  const {
    data: statusData,
    error: statusQueryError,
    loading: statusLoading,
    refetch: refetchStatus,
  } = useQuery(QUERY_BIG_QUERY_SYNC_PREVIEW_STATUS, {
    variables: { jobId: jobId ?? '' },
    skip: !jobId,
    pollInterval:
      jobId && !isTerminalBigQuerySyncJobStatus(latestJobStatusRef.current)
        ? STATUS_POLL_MS
        : undefined,
    notifyOnNetworkStatusChange: true,
  });

  const job: BigQuerySyncJobGql | null = statusData?.bigQuerySyncPreviewStatus ?? null;
  if (job?.status) {
    latestJobStatusRef.current = job.status;
  }
  const statusTerminal = isTerminalBigQuerySyncJobStatus(job?.status);

  const [startMutation, { loading: startLoading }] = useMutation(
    MUTATION_START_BIG_QUERY_SYNC_PREVIEW
  );
  const [approveMutation, { loading: approveLoading }] = useMutation(
    MUTATION_APPROVE_BIG_QUERY_SYNC_JOB
  );
  const [rejectMutation, { loading: rejectLoading }] = useMutation(
    MUTATION_REJECT_BIG_QUERY_SYNC_JOB
  );

  const resetDiffState = useCallback(() => {
    setDiffLines([]);
    setDiffNextCursor(null);
    setDiffFetchError(null);
  }, []);

  useEffect(() => {
    resetDiffState();
    setEntityInput('');
    setEntityApplied(undefined);
  }, [jobId, resetDiffState]);

  const startPreview = useCallback(async () => {
    latestJobStatusRef.current = undefined;
    setStartBlockedPayload(null);
    resetDiffState();
    const result = await startMutation();
    const payload = result.data?.startBigQuerySyncPreview;
    if (!payload) {
      throw new Error('Resposta inválida do servidor.');
    }

    if (payload.blocked) {
      setJobId(null);
      const drift = payload.drift ?? null;
      setStartBlockedPayload({ drift });
      return { blocked: true as const, drift };
    }

    setStartBlockedPayload(null);
    const nextId = payload.job?.id;
    if (!nextId) {
      throw new Error('Job não retornado pela API.');
    }
    setJobId(nextId);
    return { blocked: false as const, jobId: nextId };
  }, [resetDiffState, startMutation]);

  const applyEntityFilter = useCallback(() => {
    const trimmed = entityInput.trim();
    setEntityApplied(trimmed.length > 0 ? trimmed : undefined);
    resetDiffState();
  }, [entityInput, resetDiffState]);

  const fetchDiffPage = useCallback(
    async (cursor: string | undefined, append: boolean) => {
      if (!jobId) return;
      setDiffLoading(true);
      setDiffFetchError(null);
      try {
        const { data, error } = await client.query({
          query: QUERY_BIG_QUERY_SYNC_PREVIEW_DIFF_PAGE,
          variables: {
            jobId,
            limit: 50,
            cursor: cursor ?? undefined,
            entity: entityApplied,
          },
          fetchPolicy: 'network-only',
        });
        if (error) throw error;
        const page = data.bigQuerySyncPreviewDiffPage;
        const lines = page?.lines ?? [];
        setDiffLines((prev) => (append ? [...prev, ...lines] : lines));
        setDiffNextCursor(page?.next_cursor ?? null);
      } catch (e) {
        setDiffFetchError(e instanceof Error ? e.message : 'Erro ao carregar diff');
        if (!append) {
          setDiffLines([]);
          setDiffNextCursor(null);
        }
      } finally {
        setDiffLoading(false);
      }
    },
    [client, entityApplied, jobId]
  );

  const loadDiffFirstPage = useCallback(async () => {
    await fetchDiffPage(undefined, false);
  }, [fetchDiffPage]);

  const loadDiffNextPage = useCallback(async () => {
    if (!diffNextCursor) return;
    await fetchDiffPage(diffNextCursor, true);
  }, [diffNextCursor, fetchDiffPage]);

  const approveJob = useCallback(async () => {
    if (!jobId) return;
    await approveMutation({ variables: { jobId } });
    await refetchStatus();
  }, [approveMutation, jobId, refetchStatus]);

  const rejectJob = useCallback(async () => {
    if (!jobId) return;
    await rejectMutation({ variables: { jobId } });
    await refetchStatus();
  }, [jobId, rejectMutation, refetchStatus]);

  const resetSession = useCallback(() => {
    latestJobStatusRef.current = undefined;
    setJobId(null);
    setStartBlockedPayload(null);
    resetDiffState();
    setEntityInput('');
    setEntityApplied(undefined);
  }, [resetDiffState]);

  const selectJob = useCallback((id: string) => {
    latestJobStatusRef.current = undefined;
    setJobId(id);
    setStartBlockedPayload(null);
  }, []);

  const decisionAllowed = useMemo(
    () => canApproveOrRejectBigQuerySyncJob(job?.status, job?.blocked_reason),
    [job?.blocked_reason, job?.status]
  );

  return {
    jobId,
    job,
    statusLoading,
    statusQueryError,
    statusTerminal,
    startPreview,
    startLoading,
    startBlockedPayload,
    resetSession,
    selectJob,
    decisionAllowed,
    approveJob,
    rejectJob,
    approveLoading,
    rejectLoading,
    diffLines,
    diffNextCursor,
    diffLoading,
    diffFetchError,
    loadDiffFirstPage,
    loadDiffNextPage,
    entityInput,
    setEntityInput,
    entityApplied,
    applyEntityFilter,
  };
}
